/**
 * YouTube LiveChat Flusher — Danmaku-style scrolling live chat over the video
 *
 * Declares a single web-accessible-resource group scoped to YouTube only (www and m
 * subdomains), not to every page. The group exposes whole directories — `modules/*`,
 * `injections/*`, `styles/*`, `templates/*`, `images/*` — with no `use_dynamic_url`, so
 * each stable, vendor-named file (`modules/main.mjs`, `styles/content.css`,
 * `images/icon128.png`) resolves from a YouTube page at a fixed URL.
 *
 * Because the resources are origin-scoped, they are genuinely unreachable from anywhere
 * but YouTube — a negative from any other page says nothing about the browser, only
 * about where the probe is running. Hence `requiredOrigin`.
 *
 * Manifest verified against version 0.16.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kkjglcpgfpjlaloboikfcoofameeljbe';

export const youtubeLivechatFlusher: ExtensionDefinition = {
  id: ID,
  name: 'YouTube LiveChat Flusher (Danmaku Chat)',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 80_000,
  requiredOrigin: 'https://www.youtube.com',
  notes:
    'WAR directories scoped to youtube.com, so only YouTube can detect it. Stable ' +
    'vendor-named module/style/icon paths make detection conclusive there.',
  signals: [
    {
      id: 'war-main-mjs',
      kind: 'web-accessible-resource',
      description:
        'Fetch modules/main.mjs — the extension\'s main module, exposed only to ' +
        'YouTube origins at a fixed URL.',
      weight: 1,
      run: () => probeFetch(ID, 'modules/main.mjs'),
    },
    {
      id: 'war-icon-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/icon128.png via <img> — a vendor icon corroborating the match ' +
        'without a CORS grant.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/icon128.png'),
    },
    {
      id: 'war-content-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch styles/content.css — the chat-overlay stylesheet, a third independent ' +
        'path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'styles/content.css'),
    },
  ],
};
